/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { gql, useMutation, useQuery } from "@apollo/client";
import React from "react";
import { Location } from "@reach/router";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { Button } from "components/haven-design";

import {
  ChangeInterestStatus,
  ChangeInterestStatusVariables,
  RelatedInterestActions,
  RelatedInterestActionsVariables,
  skipInterestAction,
  skipInterestActionVariables,
  QueryInterestProfile_interest_interestList_property as PropertyType,
  syncOneSiteGuestCard,
  syncOneSiteGuestCardVariables,
  Feature,
  getUserLoginData as User,
  FormType,
  sendForm,
  sendFormVariables,
} from "types/gql-types";

import { apolloClient as client } from "data/graphql";
import logger from "services/logger";
import { InterestStatus, statusFromStage } from "model/interest/status";
import { useFeature, usePropertyFeature } from "components/feature";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { useSnackbar } from "components/haven-design/snackbar";
import ReasonModal, { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import {
  CHANGE_STATUS,
  QUERY,
  SKIP_INTEREST,
  SEND_FULL_APP,
  SYNC_ONE_SITE_GUEST_CARD,
} from "pages/dashboard/interests-profile/components/actions-query";
import SendFullAppModal from "pages/dashboard/interests/send-full-app-modal";


import styles from "./index.module.css";
import ActionItem from "./action-item";
import { MoveInModal } from "./modals/move-in";
import RequestFormsModal from "./modals/request-forms";
import ResetPasswordFormModal from "./modals/reset-password-form";

const USER_QUERY = gql`
  query getUserLoginData($id: ID!) {
    user(id: $id) {
      email
      phone
    }
  }
`;


interface Props {
  property: PropertyType;
  currentInterestName: string;
  currentInterestID: string;
  interestListID: string;
  status: string;
  setBatchActionsModalFocusedAction: React.Dispatch<React.SetStateAction<undefined>>;
  sendFullAppToInterestsEnabled: boolean;
}

const ACTIONS: Action[] = [
  Action.Add_Document,
  Action.Add_Note,
  Action.Log_Call,
  Action.Moved_In,
  Action.On_Waitlist,
  Action.Removed,
  Action.Reset_Password,
  Action.Request_Forms,
  Action.Send_Full_App,
  Action.Send_Message,
  Action.Skip,
];

const ActionButton: React.FC<Props> = (props: Props) => {
  const {
    property,
    currentInterestID,
    interestListID,
    currentInterestName,
    status: currentStatus,
    setBatchActionsModalFocusedAction,
    sendFullAppToInterestsEnabled,
  } = props;
  const { openSnackbar } = useSnackbar();

  const statusOptions = property.stages.filter((stage) => stage.enabled);

  // Local state for the confirmation modal displayed when attempting to
  // change the interest status.
  const [confirming, setConfirming] = React.useState<Action | null>(null);
  const [reason, setReason] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [isSendFullAppOpen, setIsSendFullAppOpen] = React.useState(false);

  const [isRequestFormsOpen, setIsRequestFormsOpen] = React.useState(false);
  const [isResetPasswordFormOpen, setIsResetPasswordFormOpen] = React.useState(false);

  // GraphQL queries and mutations.
  const variables = { interestID: currentInterestID, interestListID };

  const [skipInterest] = useMutation<
  skipInterestAction,
  skipInterestActionVariables
  >(SKIP_INTEREST);

  const query = useQuery<RelatedInterestActions, RelatedInterestActionsVariables>(QUERY, {
    variables,
  });

  const userQuery = useQuery<User>(
    USER_QUERY,
    { variables: { id: query?.data?.interest?.userId } },
  );

  const [setStatus] = useMutation<
  ChangeInterestStatus,
  ChangeInterestStatusVariables
  >(CHANGE_STATUS, { refetchQueries: ["activityFeed"] });

  const [sendFullApp] = useMutation<sendForm, sendFormVariables>(SEND_FULL_APP, { client });


  const [syncOneSite] = useMutation<
  syncOneSiteGuestCard,
  syncOneSiteGuestCardVariables>(SYNC_ONE_SITE_GUEST_CARD);

  const onesiteSyncEnabled = useFeature(Feature.ONESITE_SYNC);
  const hasRequestForms = usePropertyFeature(property.id, Feature.REQUEST_FORMS);
  const hasDocumentUpload = usePropertyFeature(property.id, Feature.DOCUMENT_UPLOAD);

  if (query.error || query.loading || !query?.data?.interest) {
    return null;
  }

  const { interest } = query.data;

  const onResetConfirmation = () => {
    setConfirming(null);
    setReason("");
  };

  const isActionVisible = (action: Action): boolean => {
    const status = statusFromStage(interest.status.name);
    switch (action) {
      case Action.Send_Full_App:
        return sendFullAppToInterestsEnabled;
      case Action.Request_Forms:
        return hasRequestForms ?? false;
      case Action.Moved_In:
        return status === InterestStatus.Active;
      case Action.Removed:
        return status === InterestStatus.Active;
      case Action.Reset_Password: {
        const enableToShowResetPassword = !!(
          !userQuery.error &&
          !userQuery.loading &&
          userQuery?.data?.user?.email
        );

        return enableToShowResetPassword;
      }
      case Action.Skip:
        return status === InterestStatus.Active;
      case Action.On_Waitlist:
        return status === InterestStatus.Removed || status === InterestStatus.MovedIn;
      case Action.Add_Document:
        return hasDocumentUpload ?? false;
      default:
        return true;
    }
  };

  const onSubmitNewActivity = () => {
    if (confirming === null) {
      return;
    }
    skipInterest({
      variables: {
        data: {
          resourceId: interest.id,
          note: reason || null,
        },
      },
      refetchQueries: ["QueryInterestProfile"],
    })
      .then(() => {
        openSnackbar({ message: "Success", status: "success" });
        onResetConfirmation();
      })
      .then(() => setIsSubmitting(false))
      .catch((e) => {
        logger.sendError(e);
        onResetConfirmation();
        openSnackbar({ message: "There was a problem skipping this applicant.", status: "error" });
        setIsSubmitting(false);
      });
  };

  const onStatusChange = () => {
    if (confirming === null) {
      return;
    }

    const statusVariables = {
      interestId: currentInterestID,
      status: confirming,
      reason,
    };

    const request = setStatus({ variables: statusVariables });
    request
      .then(() => query.refetch())
      .then(() => openSnackbar({ message: "Updated application status.", status: "success" }))
      .then(onResetConfirmation)
      .then(() => setIsSubmitting(false));

    request.catch((e) => {
      logger.sendError(e);
      onResetConfirmation();
      openSnackbar({ message: "There was a problem updating the application status.", status: "error" });
      setIsSubmitting(false);
    });
  };

  const onModalSubmit = () => {
    setIsSubmitting(true);

    if (confirming === "Skip") {
      onSubmitNewActivity();
    } else {
      onStatusChange();
    }
  };

  const closeSendFullApp = () => setIsSendFullAppOpen(false);
  const onSendFullApp = (deadline: Date) => {
    const fullAppVariables = {
      interestId: currentInterestID,
      type: FormType.FULL_APPLICATION,
      deadline: deadline.toISOString().slice(0, 10),
    };

    sendFullApp({ variables: fullAppVariables })
      .then(() => {
        openSnackbar({ message: "Full application request sent.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "There was a problem sending full application request.", status: "error" });
      })
      .finally(closeSendFullApp);
  };

  const onRequestForms = ():void => {
    setIsRequestFormsOpen(false);
    openSnackbar({ message: "Success", status: "success" });
  };

  const onSyncOneSite = (interestId: string) => {
    syncOneSite({ variables: { interestId } })
      .then(() => {
        openSnackbar({ message: "OneSite synced.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Failed to sync to OneSite.", status: "error" });
      });
  };

  const setActions = {
    setIsSendFullAppOpen,
    setConfirming,
    setBatchActionsModalFocusedAction,
    setIsRequestFormsOpen,
    setIsResetPasswordFormOpen,
  };

  return (
    <div className={styles.actionbuttons}>
      <Location>
        {() => (
          <>
            <SelectAutocomplete
              disableClearable
              fullWidth
              label=""
              margin="dense"
              onChange={(_, value) => setConfirming(value as Action)}
              options={statusOptions.map(option => option.name)}
              value={currentStatus}
              data-testid="changeStatusSlideout"
            />
            <div style={{ margin: "10px 0 0 10px", minWidth: "6.6em" }}>
              <PopupState variant="popover" popupId="slideout-actions-menu">
                {(popupState) => (
                  <>
                    <Button
                      id="slideout-actions"
                      size="S"
                      color="primary"
                      {...bindTrigger(popupState)}
                    >
                    Actions&nbsp;<span className="caret" style={{ marginLeft: 4 }} />
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      {ACTIONS.filter(isActionVisible).map((action) => (
                        <ActionItem
                          key={action}
                          action={action}
                          setActions={setActions}
                          closeModal={popupState.close}
                        />
                      ))}

                      {onesiteSyncEnabled &&
                        <MenuItem onClick={() => {
                          onSyncOneSite(interest.id);
                          popupState.close();
                        }}>
                          Sync to OneSite
                        </MenuItem>
                      }
                    </Menu>
                  </>
                )}
              </PopupState>
            </div>

            <SendFullAppModal
              interestName={currentInterestName}
              onSubmit={(deadline: Date) => onSendFullApp(deadline)}
              onRequestClose={closeSendFullApp}
              onCancel={closeSendFullApp}
              isOpen={isSendFullAppOpen}
            />

            <RequestFormsModal
              propertyId={property.id}
              interestId={currentInterestID}
              isOpen={isRequestFormsOpen}
              onSubmit={() => onRequestForms()}
              onRequestClose={() => setIsRequestFormsOpen(false)}
              onCancel={() => setIsRequestFormsOpen(false)}
            />

            {
              userQuery?.data?.user && (
                <ResetPasswordFormModal
                  currentInterestName={currentInterestName}
                  isOpen={isResetPasswordFormOpen}
                  user={userQuery.data.user}
                  onRequestClose={() => setIsResetPasswordFormOpen(false)}
                  onCancel={() => setIsResetPasswordFormOpen(false)}
                />
              )
            }

            <ReasonModal
              isOpen={confirming !== null && confirming !== Action.Moved_In}
              newStatus={confirming}
              currentStatus={currentStatus as Action}
              onSubmit={() => onModalSubmit()}
              onChange={(newReason) => setReason(newReason)}
              reason={reason}
              onRequestClose={() => onResetConfirmation()}
              onCancel={() => onResetConfirmation()}
              isSubmitting={isSubmitting}
            />

            <MoveInModal
              isOpen={confirming === Action.Moved_In}
              interestIds={[currentInterestID]}
              interestListId={interestListID}
              onRequestClose={() => onResetConfirmation()}
            />
          </>
        )}
      </Location>
    </div>
  );
};

export default ActionButton;
